import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import useNavigate from "src/hooks/useNavigate";
import ReactTable, { RowInfo } from "react-table";
import { bindActionCreators } from "redux";
import { TrialInstitutionMonitoringIssueDocumentsBreadcrumbs } from "src/helpers/breadcrumbHelper";
import * as MonitoringIssueDocumentActions from "../../../actions/monitoringIssueDocument";
import * as TrialInstitutionActions from "../../../actions/trialInstitution";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { RestrictedLayout } from "../../../components/layouts/index";
import { Link } from "../../../components/routing/index";
import { FontAwesomeIcon, FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "../../../constants/reactTableConstants";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { ITmdState } from "../../../reducers/index";
import { InlineLoading } from "src/components/common";
import { useParams } from "react-router";
import { MonitoringIssueSubMenu } from "../../../helpers/subMenuHelper";

import "./MonitoringIssueDocumentsPage.scss";

interface MonitoringIssueDocumentsPageProps {
    monitoringIssueDocuments: Dtos.MonitoringIssueDocument[];
    loadingMonitoringIssueDocuments: boolean;
    loadMonitoringIssueDocumentsSuccess: boolean;
    loadMonitoringIssueDocumentsFailure: boolean;
    loadMonitoringIssueDocumentsErrorCode: string;
    trialInstitution: Dtos.TrialInstitution;
    loadingTrialInstitution: boolean;
    loadTrialInstitutionSuccess: boolean;
    loadTrialInstitutionFailure: boolean;
    loadTrialInstitutionErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    lookups: Dtos.Lookup[];
    clearMonitoringIssueDocuments: MonitoringIssueDocumentActions.IMonitoringIssueDocumentClearActionCreator;
    loadMonitoringIssueDocuments: MonitoringIssueDocumentActions.IMonitoringIssueDocumentsLoadByMonitoringIssueIdActionCreator;
    clearTrialInstitution: TrialInstitutionActions.ITrialInstitutionClearActionCreator;
    loadTrialInstitution: TrialInstitutionActions.ITrialInstitutionLoadByIdActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const MonitoringIssueDocumentsPage: FC<MonitoringIssueDocumentsPageProps> = ({
    monitoringIssueDocuments,
    loadingMonitoringIssueDocuments,
    loadMonitoringIssueDocumentsSuccess,
    loadMonitoringIssueDocumentsFailure,
    loadMonitoringIssueDocumentsErrorCode,
    trialInstitution,
    loadingTrialInstitution,
    loadTrialInstitutionSuccess,
    loadTrialInstitutionFailure,
    loadTrialInstitutionErrorCode,
    permissions,
    pageSize,
    lookups,
    clearMonitoringIssueDocuments,
    loadMonitoringIssueDocuments,
    clearTrialInstitution,
    loadTrialInstitution,
    setPageSize,
    setBreadcrumbs,
    setDrawerItems,
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { trialInstitutionId } = useParams<{ trialInstitutionId: string }>();
    const { monitoringId } = useParams<{ monitoringId: string }>();
    const { monitoringIssueId } = useParams<{ monitoringIssueId: string }>();
    const trialIdNumber = parseInt(trialId, 10);
    const trialInstitutionIdNumber = parseInt(trialInstitutionId, 10); 
    const monitoringIdNumber = parseInt(monitoringId, 10);
    const monitoringIssueIdNumber = parseInt(monitoringIssueId, 10); 
    
    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- States --
    const [initalised, setInitalised] = useState<boolean>(false);

    //
    // -- Variables --
    const showMonitoringIssueDocuments = 
        !loadingMonitoringIssueDocuments && 
        loadMonitoringIssueDocumentsSuccess &&
        !loadingTrialInstitution &&
        loadTrialInstitutionSuccess;

    // Load Monitoring Issue Documents
    useEffect(() => {
        if (!initalised) {
            setBreadcrumbs(
                TrialInstitutionMonitoringIssueDocumentsBreadcrumbs(
                    trialIdNumber, 
                    trialInstitutionIdNumber, 
                    monitoringIdNumber,
                    monitoringIssueIdNumber
                )
            );

            loadMonitoringIssueDocuments(monitoringIssueIdNumber);

            setInitalised(true);
        }
    }, [
        initalised,
        trialIdNumber,
        trialInstitutionIdNumber,
        monitoringIssueIdNumber,
        monitoringIdNumber
    ]);

    // Load Trial Institution
    useEffect(() => {
        if (trialInstitutionIdNumber && !trialInstitution && !loadingTrialInstitution) {
            loadTrialInstitution(trialInstitutionIdNumber);
        }
    }, [
        trialInstitutionIdNumber,
        trialInstitution,
        loadingTrialInstitution
    ]);

    // Update drawer items & breadcrumbs
    useEffect(() => {
        if (trialInstitution) {
            setDrawerItems(
                MonitoringIssueSubMenu(
                    trialIdNumber, 
                    trialInstitutionIdNumber, 
                    monitoringIdNumber, 
                    monitoringIssueIdNumber, 
                    permissions
                )
            );
            setBreadcrumbs(
                TrialInstitutionMonitoringIssueDocumentsBreadcrumbs(
                    trialIdNumber, 
                    trialInstitutionIdNumber, 
                    monitoringIdNumber,
                    monitoringIssueIdNumber,
                    trialInstitution?.trial?.displayName, 
                    trialInstitution?.institution?.name
                )
            );
        }
    }, [
        trialIdNumber, 
        trialInstitutionIdNumber, 
        monitoringIdNumber,
        monitoringIssueIdNumber,
        trialInstitution, 
        permissions
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadMonitoringIssueDocumentsFailure) {
            navigate(`/error/${loadMonitoringIssueDocumentsErrorCode}`);
        }
        if (loadTrialInstitutionFailure) {
            navigate(`/error/${loadTrialInstitutionErrorCode}`)
        }
    }, [
        loadMonitoringIssueDocumentsFailure, 
        loadMonitoringIssueDocumentsErrorCode,
        loadTrialInstitutionFailure,
        loadTrialInstitutionErrorCode
    ]);

    // Cleanup effect to clear the monitoring issue documents & trial institution on component unmount
    useEffect(() => {
        return () => {
            clearMonitoringIssueDocuments();
            clearTrialInstitution();
        };
    }, [clearMonitoringIssueDocuments, clearTrialInstitution]);

    const renderTable = useMemo(() => {
            if (monitoringIssueDocuments?.length == 0) {
                return (
                    <div className="card card-shadow p-3">
                        <div className="alert alert-info d-flex mb-0">
                            <div className="mr-2">
                                <FontAwesomeIcon
                                    icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                    fixedWidth
                                />
                            </div>
                            <span>There are currently no documents to view.</span>
                        </div>
                    </div>
                );
            }
    
            const showPagination = monitoringIssueDocuments?.length > pageSize;
    
            return (
                <div className="table-wrapper">
                    <ReactTable
                        data={monitoringIssueDocuments}
                        pageSizeOptions={PAGE_SIZE_OPTIONS}
                        defaultPageSize={showPagination ? pageSize : monitoringIssueDocuments?.length}
                        pageSize={showPagination ? pageSize : monitoringIssueDocuments?.length}
                        showPagination={showPagination}
                        className="-striped -highlight -clickable"
                        noDataText="There are currently no documents to view"
                        resizable={false}
                        onPageSizeChange={(val) => setPageSize(val)}
                        getTdProps={(
                            _state,
                            rowInfo: RowInfo,
                            _column,
                            _instance
                        ) => {
                            return {
                                onClick: (_e, handleOriginal) => {
                                    navigate(`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/monitoring/${monitoringIdNumber}/issues/${monitoringIssueIdNumber}/documents/${rowInfo.original.id}/details`);
    
                                    if (handleOriginal) {
                                        handleOriginal();
                                    }
                                },
                            };
                        }}
                        defaultSorted={[
                            {
                                id: "name",
                                desc: false,
                            },
                        ]}
                        columns={[
                            {
                                id: "id",
                                Header: " ",
                                accessor: "id",
                                maxWidth: 50,
                                Cell: (_props) => (
                                    <FontAwesomeIcon size="lg" icon={FontAwesomeIcons.Duotone.FILE} fixedWidth />
                                ),
                            },
                            {
                                id: "name",
                                Header: "Name",
                                accessor: "name",
                            },
                        ]}
                    />
                </div>
            );
        }, [
            monitoringIssueDocuments,
            pageSize,
            PAGE_SIZE_OPTIONS,
            setPageSize,
            lookups
        ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Documents</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.MonitoringIssueDocumentCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/monitoring/${monitoringIdNumber}/issues/${monitoringIssueIdNumber}/documents/create`}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showMonitoringIssueDocuments ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showMonitoringIssueDocuments ? (
                    <div className="card">
                        <div className="patient-tables">{renderTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        AuthenticationHelper,
        permissions,
        showMonitoringIssueDocuments,
        renderTable,
        trialIdNumber,
        trialInstitutionIdNumber,
        lookups,
        monitoringIdNumber,
        monitoringIssueIdNumber
    ]);

    return (
        <RestrictedLayout
            loading={loadingMonitoringIssueDocuments || loadingTrialInstitution}
            permission={Dtos.Permission.MonitoringIssueDocumentView}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (state: ITmdState): Partial<MonitoringIssueDocumentsPageProps> => {
    let monitoringIssueDocuments: Dtos.MonitoringIssueDocument[] | undefined =
        state.monitoringIssueDocument.formData instanceof Array
            ? state.monitoringIssueDocument.formData
            : undefined;
    let trialInstitution: Dtos.TrialInstitution | undefined =
        !(state.trialInstitution.formData instanceof Array)
            ? state.trialInstitution.formData
            : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        monitoringIssueDocuments: monitoringIssueDocuments!,
        loadingMonitoringIssueDocuments:
            state.monitoringIssueDocument.loadState &&
            state.monitoringIssueDocument.loadState.status == RequestState.Pending,
        loadMonitoringIssueDocumentsSuccess:
            state.monitoringIssueDocument.loadState &&
            state.monitoringIssueDocument.loadState.status == RequestState.Success,
        loadMonitoringIssueDocumentsFailure:
            state.monitoringIssueDocument.loadState &&
            state.monitoringIssueDocument.loadState.status == RequestState.Failure,
        loadMonitoringIssueDocumentsErrorCode: state.monitoringIssueDocument.loadState.errorCode,
        trialInstitution: trialInstitution!,
        loadingTrialInstitution:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Pending,
        loadTrialInstitutionSuccess:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Success,
        loadTrialInstitutionFailure:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Failure,
        loadTrialInstitutionErrorCode: state.trialInstitution.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
        lookups: state.monitoring.lookups,
    };
};

const mapDispatchToProps = (dispatch): Partial<MonitoringIssueDocumentsPageProps> => {
    return {
        loadMonitoringIssueDocuments: bindActionCreators(MonitoringIssueDocumentActions.LoadMonitoringIssueDocumentsByMonitoringIssueId, dispatch),
        clearMonitoringIssueDocuments: bindActionCreators(MonitoringIssueDocumentActions.Clear, dispatch),
        loadTrialInstitution: bindActionCreators(TrialInstitutionActions.LoadTrialInstitutionById, dispatch),
        clearTrialInstitution: bindActionCreators(TrialInstitutionActions.Clear, dispatch),
        setPageSize: bindActionCreators(PagingActions.PageSizeChanged, dispatch),
        setBreadcrumbs: bindActionCreators(NavigationActions.SetBreadcrumbs, dispatch),
        setDrawerItems: bindActionCreators(NavigationActions.SetDrawerItems, dispatch),
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(MonitoringIssueDocumentsPage);
